import { t as tr } from "../../i18n";
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent as ReactMouseEvent } from "react";
import { fmt, rcCounterfortProfile, type Pt } from "@tasduvar/engine";
import { useCadUi } from "../../state/cadUiStore";
import { SectionDrawing, type SectionDrawingProps } from "../drawing/SectionDrawing";
import { fromSvg, toSvg, type Transform } from "../drawing/geometry";
import { snap as gridSnap } from "../drawing/snap";
import { orthoLock } from "../drawing/sketch";
import { sketchAddPoint, sketchCancel, sketchClose } from "../command/sketch";

/** 96 dpi ekranda 1 piksel (m) — durum çubuğundaki "1:n" ölçek bundan türetilir */
const PX_M = 0.0254 / 96;
const ZOOM_STEP = 1.12;
const SNAP_PX = 10;

type ViewBox = { x: number; y: number; w: number; h: number };
type Size = { width: number; height: number };
interface SnapPt { p: Pt; kind: string }

/** Model koordinatını Türkçe biçimde 3 ondalıkla yazar (durum çubuğu, ipucu) */
export const fmtCoord = (p: Pt): string => `${fmt(p.x, 3)}, ${fmt(p.y, 3)}`;

/**
 * Model alanı (README §2.8, §5.2): `SectionDrawing` üstünde pan/zoom/yakalama/crosshair.
 * Pencere `cadUi.viewBox` (null = sığdır); ekran→SVG dönüşümü aspect korunmuş viewBox ile; tekerlek imleç altındaki noktayı sabit tutar.
 * Sol sürükleme boş alanda (ve Kaydır kipinde her yerde) kaydırır, orta tuş her kipte; ölçü/köşe düzenleyicileri etkilenmez.
 * Klavye: Ctrl+Ok kaydır, Ctrl +/- yakınlaştır, Ctrl+0 sığdır, Esc kaydırma kipinden çık.
 */
export function ModelSpace(props: Omit<SectionDrawingProps, "viewBox" | "svgRef" | "svgProps" | "overlay" | "onScene">): JSX.Element {
  const svgRef = useRef<SVGSVGElement>(null);
  const viewBox = useCadUi((s) => s.viewBox), setViewBox = useCadUi((s) => s.setViewBox);
  const status = useCadUi((s) => s.status);
  const setCursor = useCadUi((s) => s.setCursor), setScale = useCadUi((s) => s.setScale);
  const zoomTo = useCadUi((s) => s.zoomTo), setZoomTo = useCadUi((s) => s.setZoomTo);
  const panMode = useCadUi((s) => s.panMode), setPanMode = useCadUi((s) => s.setPanMode);
  const sketch = useCadUi((s) => s.sketch);
  const [scene, setScene] = useState<{ t: Transform; size: Size } | null>(null);
  const [cur, setCur] = useState<{ p: Pt; m: Pt; snap: SnapPt | null } | null>(null);   // SVG koordinatı, gösterilen model koordinatı, yakalama
  const [dragging, setDragging] = useState(false);
  const drag = useRef<{ px: number; py: number; vx: number; vy: number } | null>(null);
  const onScene = useCallback((t: Transform, size: Size) => setScene({ t, size }), []);
  const fitVb = (): ViewBox => ({ x: 0, y: 0, w: scene?.size.width ?? 720, h: scene?.size.height ?? 480 });
  const vb: ViewBox = viewBox ?? fitVb();

  // Yakalama noktaları: köşeler (uç) + kenar ortaları (orta) — çokgen, diş, payanda, sanal düzlem tepesi
  const snapPts = useMemo<SnapPt[]>(() => {
    const r = props.result; if (!r) return [];
    const g = r.geometry;
    const rings: Pt[][] = [g.polygon, ...(g.key ? [g.key] : [])];
    const cf = props.wall.geometry.mode === "rc-cantilever" ? rcCounterfortProfile(props.wall.geometry) : null;
    if (cf) rings.push(cf);
    const out: SnapPt[] = [{ p: g.virtualPlane.top, kind: "uç" }];
    for (const ring of rings) ring.forEach((p, i) => { const q = ring[(i + 1) % ring.length]!; out.push({ p, kind: "uç" }, { p: { x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 }, kind: "orta" }); });
    return out;
  }, [props.result, props.wall]);

  /** ekran → SVG (aspect korunmuş viewBox; k = SVG birimi / ekran pikseli) */
  const svgPt = (e: { clientX: number; clientY: number }): { x: number; y: number; k: number } => {
    const rect = svgRef.current!.getBoundingClientRect();
    const k = Math.max(vb.w / rect.width, vb.h / rect.height);
    return { x: vb.x + vb.w / 2 + (e.clientX - rect.left - rect.width / 2) * k, y: vb.y + vb.h / 2 + (e.clientY - rect.top - rect.height / 2) * k, k };
  };
  const isInteractive = (el: EventTarget | null): boolean => el instanceof Element && !!el.closest("foreignObject,[data-vertex],[data-edge],[data-handle],button,input,select");

  const onMouseMove = (e: ReactMouseEvent<SVGSVGElement>): void => {
    if (!svgRef.current || !scene) return;
    const p = svgPt(e);
    if (drag.current) { const d = drag.current; setViewBox({ ...vb, x: d.vx - (p.x - d.px), y: d.vy - (p.y - d.py) }); return; }
    let snapHit: SnapPt | null = null;
    if (status.YAKALA) {
      const th = SNAP_PX * p.k; let best = th;
      for (const s of snapPts) { const q = toSvg(scene.t, s.p); const d = Math.hypot(q.x - p.x, q.y - p.y); if (d < best) { best = d; snapHit = s; } }
    }
    const shown = snapHit ? toSvg(scene.t, snapHit.p) : { x: p.x, y: p.y };
    const m = snapHit ? snapHit.p : status.SNAP ? (({ x, y }) => ({ x: gridSnap(x), y: gridSnap(y) }))(fromSvg(scene.t, shown)) : fromSvg(scene.t, shown);
    setCur({ p: shown, m, snap: snapHit });
    setCursor({ x: m.x, y: m.y, snap: snapHit?.kind });
  };
  const onMouseDown = (e: ReactMouseEvent<SVGSVGElement>): void => {
    if (!svgRef.current) return;
    if (sketch && e.button === 0 && cur && !panMode) { e.preventDefault(); sketchAddPoint(status.ORTHO ? orthoLock(sketch.points[sketch.points.length - 1], cur.m) : cur.m); return; }
    const pan = e.button === 1 || (e.button === 0 && (panMode || !isInteractive(e.target)));
    if (!pan) return;
    e.preventDefault();
    const p = svgPt(e);
    drag.current = { px: p.x, py: p.y, vx: vb.x, vy: vb.y }; setDragging(true);
  };
  const endDrag = (): void => { if (drag.current) { drag.current = null; setDragging(false); } };
  const onMouseLeave = (): void => { endDrag(); setCur(null); setCursor(null); };

  // Tekerlek: passive olmayan dinleyici (sayfa kaymasın); imleç altındaki nokta sabit
  const vbRef = useRef(vb); vbRef.current = vb;
  const sceneRef = useRef(scene); sceneRef.current = scene;
  useEffect(() => {
    const svg = svgRef.current; if (!svg) return;
    const onWheel = (e: WheelEvent): void => {
      e.preventDefault();
      const v = vbRef.current, fit = sceneRef.current?.size ?? { width: 720, height: 480 };
      const rect = svg.getBoundingClientRect();
      const k = Math.max(v.w / rect.width, v.h / rect.height);
      const px = v.x + v.w / 2 + (e.clientX - rect.left - rect.width / 2) * k, py = v.y + v.h / 2 + (e.clientY - rect.top - rect.height / 2) * k;
      const f = e.deltaY > 0 ? ZOOM_STEP : 1 / ZOOM_STEP;
      const w = Math.min(fit.width * 4, Math.max(fit.width / 8, v.w * f)), h = w * (v.h / v.w);
      setViewBox({ x: px - (px - v.x) * (w / v.w), y: py - (py - v.y) * (h / v.h), w, h });
    };
    svg.addEventListener("wheel", onWheel, { passive: false });
    return () => svg.removeEventListener("wheel", onWheel);
  }, [setViewBox]);

  // Ölçek göstergesi: 1 ekran pikseli = k SVG birimi = k / t.scale m → 1:n
  useEffect(() => {
    const svg = svgRef.current; if (!svg || !scene) return;
    const rect = svg.getBoundingClientRect(); if (rect.width < 1) return;
    const k = Math.max(vb.w / rect.width, vb.h / rect.height);
    setScale(Math.max(1, Math.round(k / scene.t.scale / PX_M)));
  }, [vb.x, vb.y, vb.w, vb.h, scene, setScale]);

  // ZOOM <n>: istenen ölçeğe göre pencere genişliği, merkez sabit
  useEffect(() => {
    const svg = svgRef.current; if (zoomTo === null || !svg || !scene) return;
    const rect = svg.getBoundingClientRect();
    const k = zoomTo * PX_M * scene.t.scale;
    const w = k * rect.width, h = k * rect.height;
    setViewBox({ x: vb.x + vb.w / 2 - w / 2, y: vb.y + vb.h / 2 - h / 2, w, h });
    setZoomTo(null);
  }, [zoomTo, scene, vb, setViewBox, setZoomTo]);

  const zoomBy = (f: number): void => { const fit = scene?.size ?? { width: 720, height: 480 }; const w = Math.min(fit.width * 4, Math.max(fit.width / 8, vb.w * f)), h = w * (vb.h / vb.w); setViewBox({ x: vb.x + vb.w / 2 - w / 2, y: vb.y + vb.h / 2 - h / 2, w, h }); };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === "Escape" && sketch) { sketchCancel(); return; }
    if (e.key === "Enter" && sketch && !isInteractive(e.target)) { sketchClose(); return; }
    if (e.key === "Escape" && panMode) { setPanMode(false); return; }
    if (!e.ctrlKey || isInteractive(e.target)) return;
    const step = vb.w * 0.1;
    const moves: Record<string, () => void> = {
      ArrowLeft: () => setViewBox({ ...vb, x: vb.x - step }), ArrowRight: () => setViewBox({ ...vb, x: vb.x + step }),
      ArrowUp: () => setViewBox({ ...vb, y: vb.y - step }), ArrowDown: () => setViewBox({ ...vb, y: vb.y + step }),
      "+": () => zoomBy(1 / ZOOM_STEP), "=": () => zoomBy(1 / ZOOM_STEP), "-": () => zoomBy(ZOOM_STEP), "0": () => setViewBox(null),
    };
    const fn = moves[e.key]; if (fn) { e.preventDefault(); fn(); }
  };

  return (
    <div className="absolute inset-0 outline-none" tabIndex={0} onKeyDown={onKeyDown} aria-label={tr("Model alanı")} data-pan={panMode || undefined}>
      <SectionDrawing {...props} viewBox={viewBox} svgRef={svgRef} onScene={onScene}
        svgProps={{ onMouseMove, onMouseDown, onMouseUp: endDrag, onMouseLeave, onAuxClick: (e) => e.preventDefault(), onContextMenu: (e) => { if (sketch) { e.preventDefault(); sketchClose(); } }, style: { cursor: dragging ? "grabbing" : panMode ? "grab" : "crosshair" } }}
        overlay={(t, v) => { const k = v.w / (svgRef.current?.getBoundingClientRect().width || v.w); return (<>
          {sketch ? <SketchOverlay t={t} k={k} points={sketch.points} cur={cur && !dragging ? (status.ORTHO ? orthoLock(sketch.points[sketch.points.length - 1], cur.m) : cur.m) : null} /> : null}
          {cur && !dragging ? <Overlay vb={v} cur={cur} k={k} dyn={status.DYN} /> : null}
        </>); }} />
    </div>
  );
}

/** Crosshair (tam genişlik/yükseklik kesikli), yakalama karesi (12 px yeşil) ve mono ipucu; k = SVG birimi / ekran pikseli (ekran boyutu sabit kalsın) */
function Overlay({ vb, cur, k, dyn }: { vb: ViewBox; cur: { p: Pt; m: Pt; snap: SnapPt | null }; k: number; dyn: boolean }): JSX.Element {
  const { p, m, snap } = cur;
  const text = snap ? `${snap.kind} ${tr("yakala")} · ${fmtCoord(m)}` : dyn ? fmtCoord(m) : null;
  const fs = 11 * k, pad = 4 * k, tw = text ? text.length * 6.6 * k + 2 * pad : 0;
  return (
    <g data-part="overlay" pointerEvents="none" aria-hidden>
      <line x1={vb.x} y1={p.y} x2={vb.x + vb.w} y2={p.y} stroke="#6E7680" strokeDasharray={`${k} ${3 * k}`} vectorEffect="non-scaling-stroke" />
      <line x1={p.x} y1={vb.y} x2={p.x} y2={vb.y + vb.h} stroke="#6E7680" strokeDasharray={`${k} ${3 * k}`} vectorEffect="non-scaling-stroke" />
      {snap ? <rect x={p.x - 6 * k} y={p.y - 6 * k} width={12 * k} height={12 * k} fill="none" stroke="#1D7A46" strokeWidth={1.6} vectorEffect="non-scaling-stroke" /> : null}
      {text ? (
        <g transform={`translate(${p.x + 10 * k} ${p.y + 6 * k})`}>
          <rect width={tw} height={fs + 2 * pad} fill="#23272C" stroke="#4A5058" vectorEffect="non-scaling-stroke" />
          <text x={pad} y={pad + fs * 0.82} fontSize={fs} fill={snap ? "#7FB584" : "#D7DBE0"} fontFamily="'Red Hat Mono', ui-monospace, monospace" style={{ fontVariantNumeric: "tabular-nums" }}>{text}</text>
        </g>
      ) : null}
    </g>
  );
}

/** CIZ kipi: girilen köşeler, kapalı olmayan çizgi ve son noktadan imlece lastik bant (uzunluk / açı etiketi). */
function SketchOverlay({ t, k, points, cur }: { t: Transform; k: number; points: Pt[]; cur: Pt | null }): JSX.Element {
  const last = points[points.length - 1];
  const s = points.map((p) => toSvg(t, p));
  const c = cur ? toSvg(t, cur) : null;
  const fs = 11 * k;
  const band = last && cur ? { L: Math.hypot(cur.x - last.x, cur.y - last.y), ang: (Math.atan2(cur.y - last.y, cur.x - last.x) * 180) / Math.PI } : null;
  return (
    <g data-part="sketch" pointerEvents="none" aria-hidden>
      {s.length > 1 ? <polyline points={s.map((p) => `${p.x},${p.y}`).join(" ")} fill="none" stroke="#FFD166" strokeWidth={2} vectorEffect="non-scaling-stroke" /> : null}
      {s.length > 2 && s[0] && last ? <line x1={s[s.length - 1]!.x} y1={s[s.length - 1]!.y} x2={s[0].x} y2={s[0].y} stroke="#FFD166" strokeDasharray={`${4 * k} ${3 * k}`} strokeOpacity={0.5} vectorEffect="non-scaling-stroke" /> : null}
      {last && c ? <line x1={s[s.length - 1]!.x} y1={s[s.length - 1]!.y} x2={c.x} y2={c.y} stroke="#FFD166" strokeDasharray={`${6 * k} ${3 * k}`} vectorEffect="non-scaling-stroke" /> : null}
      {s.map((p, i) => <g key={i}><rect x={p.x - 4 * k} y={p.y - 4 * k} width={8 * k} height={8 * k} fill="#1B1E22" stroke="#FFD166" vectorEffect="non-scaling-stroke" /><text x={p.x + 6 * k} y={p.y - 6 * k} fontSize={fs} fill="#FFD166" fontFamily="'Red Hat Mono', ui-monospace, monospace">{i + 1}</text></g>)}
      {band && c ? <text x={(s[s.length - 1]!.x + c.x) / 2 + 8 * k} y={(s[s.length - 1]!.y + c.y) / 2 - 8 * k} fontSize={fs} fill="#FFD166" fontFamily="'Red Hat Mono', ui-monospace, monospace" style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(band.L, 3)} m ∠{fmt(band.ang, 1)}°</text> : null}
    </g>
  );
}
